"use client";

import React, { useEffect } from "react";
import { Phone, Mail, Clock, Shield, CircleCheck, CircleAlert } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const SERVICE_OPTIONS = ["Remodelling", "Tiling", "Painting", "Vinyl Flooring", "Laminate Flooring", "Hardwood Flooring", "Plumbing", "Power Washing", "Carpentry", "Drywall Repairs and Installation", "Snow Removal", "Other"];
function CompanionForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control,
    watch,
    setValue
  } = useWvcForm();
  const selectedOccasion = watch("occasion");

  // Read service from query params on mount and preselect if it matches
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const serviceParam = params.get("service");
    if (serviceParam) {
      const match = SERVICE_OPTIONS.find(opt => opt.toLowerCase() === serviceParam.trim().toLowerCase());
      if (match) {
        setValue("occasion", match, {
          shouldValidate: true
        });
      }
    }
  }, [setValue]);
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-start py-8 space-y-5">
        <div className="size-12 rounded border border-border bg-muted flex items-center justify-center">
          <CircleCheck className="size-6 text-primary" />
        </div>
        <div className="space-y-1.5">
          <h3 className="text-2xl font-serif font-normal text-foreground">
            Request received
          </h3>
          <p className="text-muted-foreground text-sm leading-relaxed max-w-md">
            {successMessage}
          </p>
        </div>
        <Button onClick={resetForm} variant="outline" className="h-10 px-6 border-border hover:bg-muted text-foreground rounded text-sm font-sans">
          Send another request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="full_name" rules={{
        required: "Full Name is required"
      }} render={({
        field
      }) => <FormItem className="space-y-1.5">
              <FormLabel className="text-foreground text-sm font-sans font-medium">
                Your name
              </FormLabel>
              <FormControl>
                <Input placeholder="John Smith" className="h-11 bg-background border-input text-foreground placeholder:text-muted-foreground focus-visible:border-primary focus-visible:ring-primary rounded" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem className="space-y-1.5">
              <FormLabel className="text-foreground text-sm font-sans font-medium">
                Email
              </FormLabel>
              <FormControl>
                <Input type="email" placeholder="name@example.com" className="h-11 bg-background border-input text-foreground placeholder:text-muted-foreground focus-visible:border-primary focus-visible:ring-primary rounded" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="phone" rules={{
        required: "Phone Number is required"
      }} render={({
        field
      }) => <FormItem className="space-y-1.5">
              <FormLabel className="text-foreground text-sm font-sans font-medium">
                Phone
              </FormLabel>
              <FormControl>
                <Input type="tel" placeholder="(571) 249-6482" className="h-11 bg-background border-input text-foreground placeholder:text-muted-foreground focus-visible:border-primary focus-visible:ring-primary rounded" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="occasion" rules={{
        required: "Please select a service type"
      }} render={({
        field
      }) => <FormItem className="space-y-1.5">
              <FormLabel className="text-foreground text-sm font-sans font-medium">
                Service
              </FormLabel>
              <Select onValueChange={field.onChange} value={field.value || ""}>
                <FormControl>
                  <SelectTrigger className="h-11 bg-background border-input text-foreground focus-visible:border-primary focus-visible:ring-primary w-full rounded">
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="bg-popover border-border text-popover-foreground">
                  {SERVICE_OPTIONS.map((option, i) => <SelectItem key={i} value={option} data-index={i}>
                      {option}
                    </SelectItem>)}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>} />
      </div>

      {selectedOccasion === "Other" && <FormField control={control} name="other_service_type" rules={{
      required: selectedOccasion === "Other" ? "Please specify the service type" : false
    }} render={({
      field
    }) => <FormItem className="space-y-1.5">
              <FormLabel className="text-foreground text-sm font-sans font-medium">
                Specify service type
              </FormLabel>
              <FormControl>
                <Input placeholder="Describe the trade or scope" className="h-11 bg-background border-input text-foreground placeholder:text-muted-foreground focus-visible:border-primary focus-visible:ring-primary rounded" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="event_date" rules={{
        required: "Preferred Date is required"
      }} render={({
        field
      }) => <FormItem className="space-y-1.5">
              <FormLabel className="text-foreground text-sm font-sans font-medium">
                Preferred start date
              </FormLabel>
              <FormControl>
                <Input type="date" className="h-11 bg-background border-input text-foreground focus-visible:border-primary focus-visible:ring-primary block w-full rounded" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="duration_hours" rules={{
        required: "Estimated duration is required",
        min: {
          value: 1,
          message: "Minimum 1 week"
        },
        max: {
          value: 52,
          message: "Maximum 52 weeks"
        }
      }} render={({
        field
      }) => <FormItem className="space-y-1.5">
              <FormLabel className="text-foreground text-sm font-sans font-medium">
                Estimated timeline (weeks)
              </FormLabel>
              <FormControl>
                <Input type="number" min="1" max="52" placeholder="e.g. 2" className="h-11 bg-background border-input text-foreground placeholder:text-muted-foreground focus-visible:border-primary focus-visible:ring-primary rounded" {...field} />
              </FormControl>
              <FormDescription className="text-xs text-muted-foreground">
                Rough estimate in weeks from start to finish.
              </FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="additional_details" render={({
      field
    }) => <FormItem className="space-y-1.5">
            <FormLabel className="text-foreground text-sm font-sans font-medium">
              Details
            </FormLabel>
            <FormControl>
              <Textarea placeholder="Describe the job: rooms, size, what you want done" className="resize-none bg-background border-input text-foreground placeholder:text-muted-foreground focus-visible:border-primary focus-visible:ring-primary min-h-[110px] rounded" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-3 bg-destructive/10 border border-destructive/30 rounded flex items-start gap-2.5">
          <CircleAlert className="size-4 text-destructive shrink-0 mt-0.5" />
          <p className="text-sm text-destructive font-sans">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-11 px-6 text-sm font-sans font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-colors rounded">
        {isSubmitting ? "Sending..." : "Request a visit"}
      </Button>

      <p className="text-xs text-muted-foreground pt-1 flex items-center justify-center gap-1.5">
        <Shield className="size-3.5 text-primary" />
        <span>Licensed &amp; insured contracting services</span>
      </p>
    </Form>;
}
export default function SecureYourSlot() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="241608" id="secure-your-slot" className="bg-background text-foreground py-14 md:py-20 lg:py-24 border-b border-border" ref={sectionRef}>
      <div className="container mx-auto w-full max-w-7xl px-4 md:px-6 lg:px-8">
        <div className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-start transition-opacity duration-300 ${isVisible ? "opacity-100" : "opacity-0"}`}>
          {/* Left Column: Direct Info & Contact */}
          <div className="lg:col-span-5 space-y-6">
            <div className="space-y-3">
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-normal leading-tight text-foreground border-l-4 border-primary pl-4">
                Book a visit
              </h1>
              <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
                Tell us what you need done and when you want to start. We will get back to you within 24 hours to schedule an on-site walkthrough.
              </p>
            </div>

            <div className="border border-border rounded p-5 space-y-4 bg-card">
              <div className="flex items-start gap-3">
                <Phone className="size-5 text-primary shrink-0 mt-0.5" />
                <div>
                  <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Direct phone</div>
                  <a href="tel:+15712496482" className="text-lg font-medium text-foreground hover:text-primary transition-colors inline-block mt-0.5">
                    +1 (571) 249-6482
                  </a>
                </div>
              </div>

              <div className="border-t border-border pt-4 flex items-start gap-3">
                <Clock className="size-5 text-primary shrink-0 mt-0.5" />
                <div>
                  <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Hours</div>
                  <p className="text-sm text-foreground mt-0.5">
                    Mon-Sat, 7am - 7pm
                  </p>
                </div>
              </div>

              <div className="border-t border-border pt-4 flex items-start gap-3">
                <Mail className="size-5 text-primary shrink-0 mt-0.5" />
                <div>
                  <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Email</div>
                  <a href="mailto:princeowusu989@yahoo.com" className="text-sm text-foreground hover:text-primary transition-colors break-all mt-0.5 inline-block">
                    princeowusu989@yahoo.com
                  </a>
                </div>
              </div>
            </div>

            <div className="border border-border rounded overflow-hidden">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1685663/img/construction_site_modern.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1685663/img/construction_site_modern.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1685663/img/construction_site_modern.png-wvc-sizes" alt="Ablaze Contracting work site" className="w-full aspect-[16/10] object-cover" loading="lazy" />
            </div>
          </div>

          {/* Right Column: Project Intake Form */}
          <div className="lg:col-span-7">
            <div className="bg-card border border-border rounded p-6 sm:p-8">
              <div className="mb-6 border-b border-border pb-4">
                <h2 className="text-xl sm:text-2xl font-serif font-normal text-foreground">
                  Project intake
                </h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Fill in your details below. We review every request directly.
                </p>
              </div>

              <FormProvider formKey="secure_slot_form_book_appointment" formId="secure_slot_form_book_appointment" sectionName="Secure Your Slot" defaultValues={{
              full_name: "",
              email: "",
              phone: "",
              occasion: "",
              other_service_type: "",
              event_date: "",
              duration_hours: "",
              additional_details: ""
            }} validationRules={{
              full_name: {
                required: true
              },
              email: {
                required: true,
                pattern: "email"
              },
              phone: {
                required: true
              },
              occasion: {
                required: true
              },
              other_service_type: {
                custom: (value, values) => {
                  if (values?.occasion === "Other" && !value) {
                    return "Please specify the service type";
                  }
                  return null;
                }
              },
              event_date: {
                required: true
              },
              duration_hours: {
                required: true,
                min: 1,
                max: 52
              }
            }} submitText="Request a visit" successMessage="Your project request has been logged. We will contact you within 24 hours to arrange a walkthrough." errorMessage="Submission failed. Please check your connection or call us directly.">
                <CompanionForm />
              </FormProvider>
            </div>
          </div>
        </div>
      </div>
    </section>;
}